import { FC, useCallback, useRef, useState } from 'react';
import { PhoneInputProps } from 'react-international-phone';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import MuiLoadingButton from '@mui/lab/LoadingButton';
import useTranslation from 'next-translate/useTranslation';

import {
  OnboardingModalActions,
  useOnboardingModalActionsCompact,
} from '@/components/MainModal/OnboardingModalActions';
import { PhoneNumberInput } from '@/components/Steps/PhoneNumberStep/PhoneNumberInput';
import { useAppSelector, useCountryCode, useOnboardingTask, useTracking, useTraits } from '@/hooks';
import {
  selectCompanyId,
  selectHubspotContactId,
  selectUserEmail,
  setHubspotContactId,
} from '@/redux/features/authSlice';
import { setPhoneNumber } from '@/redux/features/onboardingSlice';
import CompanyRequests from '@/requests/company.requests';
import HubspotRequests from '@/requests/hubspot.requests';
import { FlexBox } from '@/shared/FlexBox';
import { StepCommonProps } from '@/types/steps.type';
import { isPhoneValidLazy } from '@/utils/phone-validity.util';
import SentryService from '@/utils/sentry-service.util';

type PhoneState = {
  phone: string;
  countryCode: string | undefined;
}

export const PhoneNumberStepComponent: FC<StepCommonProps> = ({ flow }) => {
  const { t } = useTranslation();
  const { countryCode } = useCountryCode();
  const { goToNextStep, goToPreviousStep } = useOnboardingTask(flow);
  const { trackAppEvent } = useTracking(flow);
  const { updateTraits } = useTraits();
  const { company } = useAppSelector(state => state.auth);
  const hubspotContactId = useSelector(selectHubspotContactId);
  const userEmail = useSelector(selectUserEmail);
  const isGoogleAuthUser = company?.splitTest?.some(st => st.name === 'google-auth' && st.value);

  const [formState, setFormState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  const storedPhone = useAppSelector(state => state.onboarding.phoneNumber);
  const backendPhone = company?.companySettings?.custom?.phoneNumber;
  const [phoneState, setPhoneState] = useState<PhoneState>({
    phone: storedPhone || backendPhone || '',
    countryCode: countryCode ?? 'us',
  });
  const companyId = useSelector(selectCompanyId);
  const dispatch = useDispatch();
  const compactActions = useOnboardingModalActionsCompact();
  const lastSyncedHubspotPhone = useRef<string | null>(null);

  const syncHubspotPhone = useCallback(async (phone: string) => {
    if (!isGoogleAuthUser || !phone) return;

    if (lastSyncedHubspotPhone.current === phone) return;

    const contactId = hubspotContactId
      || (userEmail ? await HubspotRequests.getContactIdByEmail(userEmail) : undefined);

    if (!contactId) return;

    try {
      lastSyncedHubspotPhone.current = phone;
      await HubspotRequests.updateContactProperties(contactId, { phone });
      if (!hubspotContactId) {
        dispatch(setHubspotContactId(contactId));
      }
    } catch (err) {
      lastSyncedHubspotPhone.current = null;
      await SentryService.captureExceptionWithRetryAfter(err, {
        errorContext: 'Failed to sync HubSpot phone number',
        contactId,
      });
    }
  }, [dispatch, hubspotContactId, isGoogleAuthUser, userEmail]);

  async function handleCompleteSetup() {
    setFormState('loading');
    const isValid = await isPhoneValidLazy(phoneState.phone);
    if (!isValid) {
      handleInvalidPhone();
      return;
    }

    try {
      updateTraits({
        companyTraits: {
          onboarding_step: 'Challenge Screen',
        },
        userTraits: { phonenumber: phoneState.phone },
        sendSegment: true,
      });
      trackAppEvent('Added Phone Number On Onboarding', {
        phonenumber: phoneState.phone,
        countrycode: phoneState.countryCode,
      });

      persistPhoneNumber();
      await syncHubspotPhone(phoneState.phone);
      await goToNextStep();
    } finally {
      setFormState('idle');
    }
  }

  const handleBackButtonEvent = () => {
    persistPhoneNumber();
    goToPreviousStep();
  };

  const persistPhoneNumber = () => {
    CompanyRequests.putCompanySettings(companyId,
      { custom: { phoneNumber: phoneState.phone }});

    dispatch(setPhoneNumber(phoneState.phone));
  };


  function handleInvalidPhone() {
    setFormState('error');
    setError(t('onboarding:phone_number_step.errors.invalid_phone'));
    trackAppEvent('Unsuccessful Phone Number Submission', {
      phonenumber: phoneState.phone,
      error: 'Invalid Phone Number',
    });
  }

  const handlePhoneChange: PhoneInputProps['onChange'] = (phone, { country }) => {
    if (error) setError(undefined);

    setPhoneState({
      phone,
      countryCode: country.iso2,
    });
  };

  return (
    <FlexBox flexDirection="column" alignItems="center" rowGap={ 7 }>
      <PhoneInputWrapper>
        <PhoneNumberInput
          renderIf={ !!countryCode }
          phone={ phoneState.phone }
          onChange={ handlePhoneChange }
          countryCode={ countryCode }
          error={ error }
        />
      </PhoneInputWrapper>
      <OnboardingModalActions>
        <FlexBox
          column={ false }
          gap={ 3 }
          justifyContent="space-between"
          width="100%"
          mt={ compactActions ? 0 : 4 }
        >
          <MuiLoadingButton
            variant="outlined"
            onClick={ () => handleBackButtonEvent() }
          >
            { t('common:back') }
          </MuiLoadingButton>
          <MuiLoadingButton
            variant="contained"
            loading={ formState === 'loading' }
            onClick={ handleCompleteSetup }
            aria-label="Complete Setup"
          >
            { t('onboarding:phone_number_step.submit_button') }
          </MuiLoadingButton>
        </FlexBox>
      </OnboardingModalActions>
    </FlexBox>
  );
};

const PhoneInputWrapper = styled.div`
  margin-top: 20px;
  height: 36px;
`;
